import { Loading } from '@/components/loading';
import { useGenerateProjectReport } from '@/modules/Report/Report.api';
import { NoPdf } from '@/modules/Report/Report.nopdf';
import { PdfViewer } from '@/modules/Report/Report.pdfviewer';
import { reportStatus, ReportStatusData } from '@/modules/Report/Report.schema';
import { QueryStatus } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

interface ProjectReportProps {
  projectId: string;
  visibleTab: boolean;
  data: ReportStatusData | undefined;
  status: QueryStatus;
  refetch: () => void;
  setRefetching: (refetching: boolean) => void;
}

export const ProjectReport = ({ projectId, visibleTab, data, status, refetch, setRefetching }: ProjectReportProps) => {
  const generateReport = useGenerateProjectReport();
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const generatePdf = useCallback(async () => {
    setGeneratingPdf(true);
    try {
      await generateReport.mutateAsync(projectId);
      toast.success('Report sent for generation.');
      setRefetching(true);
      refetch();
    } catch (error) {
      toast.error((error as Error).message || 'Report generation failed.', {
        autoClose: 3000,
      });
      console.error(error);
    }
    setGeneratingPdf(false);
  }, [generateReport, projectId, setRefetching, refetch]);

  if (status === 'error') {
    toast.error('Failed to fetch project report status');
    return;
  }

  if (status === 'pending' || !data) {
    return <Loading />;
  }

  return (
    <>
      {visibleTab && data.status !== reportStatus.COMPLETED && (
        <NoPdf status={data.status} generatePdf={generatePdf} generatingPdf={generatingPdf} />
      )}
      <PdfViewer
        visibleTab={visibleTab && data.status === reportStatus.COMPLETED}
        src={data.url}
        generatePdf={generatePdf}
        generatingPdf={generatingPdf}
      />
    </>
  );
};
